import { datadogRum } from '@datadog/browser-rum-slim'
import { getEnv } from '@unni/platform'

import { DATADOG_ENV, DATADOG_SERVICE_NAME } from './datadog-constants'

datadogRum.init({
  applicationId: 'e4cec37c-384d-43e0-89fa-1e8f9e7f89d7',
  clientToken: 'pubdd37bc1c135189b619d207ce86fd629e',
  version: getEnv('NEXT_PUBLIC_VERSION'),
  site: 'datadoghq.com',
  service: DATADOG_SERVICE_NAME,
  env: DATADOG_ENV,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 0,
  trackLongTasks: true,
  trackResources: true,
  defaultPrivacyLevel: 'mask-user-input',
  traceSampleRate: 30,
  trackSessionAcrossSubdomains: true,
  // Enhanced error tracking for better APM correlation
  trackUserInteractions: true,
  // Enable error collection with stack traces
  beforeSend: (event: any) => {
    // Enhance error events with additional context
    if (event.type === 'error') {
      // Add custom tags for better filtering in Datadog
      event.context = {
        ...event.context,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      }

      // Ensure error has proper structure for APM correlation
      if (event.error) {
        event.error.fingerprint = event.error.stack
          ? btoa(event.error.stack.split('\n')[0]).substring(0, 16)
          : 'no-stack-' + Math.random().toString(36).substring(7)
      }
    }

    return true
  },
  allowedTracingUrls: [
    // Include current host for trace correlation
    typeof window !== 'undefined' ? window.location.origin : '',
    getEnv('NEXT_PUBLIC_HOST'),
    getEnv('NEXT_PUBLIC_SOLAR_API_HOST'),
    getEnv('NEXT_PUBLIC_DEBRIS_ENDPOINT'),
  ].filter(Boolean) as string[],
})

if (typeof window !== 'undefined') {
  // Add global error handler to capture unhandled errors
  window.addEventListener('error', (event) => {
    console.error('Global error captured for RUM:', {
      message: event.error?.message || event.message,
      stack: event.error?.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      timestamp: new Date().toISOString(),
    })

    // Send to RUM with additional context
    datadogRum.addError(event.error || new Error(event.message), {
      errorType: 'javascript_error',
      source: 'global_handler',
      filename: event.filename,
      line: event.lineno,
      column: event.colno,
    })
  })

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection captured for RUM:', {
      reason: event.reason,
      timestamp: new Date().toISOString(),
    })

    // Convert to Error object if needed
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(typeof event.reason === 'string' ? event.reason : 'Unhandled promise rejection')

    datadogRum.addError(error, {
      errorType: 'unhandled_promise_rejection',
      source: 'global_handler',
      originalReason: event.reason,
    })
  })
}

// Helper function to manually track errors with trace correlation
export const trackErrorWithTrace = (error: Error, context?: any) => {
  datadogRum.addError(error, {
    ...context,
    timestamp: new Date().toISOString(),
    source: 'manual_tracking',
  })
}

// Helper to add user context for better error attribution
export const setRumUserContext = (user: { id?: string; name?: string; email?: string }) => {
  datadogRum.setUser(user)
}

// Helper to add custom attributes for error filtering
export const addRumAttribute = (key: string, value: any) => {
  // Use setGlobalContextProperty for adding custom attributes
  datadogRum.setGlobalContextProperty(key, value)
}
